import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { ArrowLeft, Phone, InstagramLogo, Envelope, Truck, ShieldCheck } from "@phosphor-icons/react";

const SECTION = "mt-8 first:mt-0";
const H2 = "font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] mb-3 leading-tight";
const P = "text-sm sm:text-base text-[#3F2D1B] leading-relaxed mb-3";
const UL = "list-disc pl-6 space-y-1.5 mb-4 text-sm sm:text-base text-[#3F2D1B]";

const SUPPORT_PHONE_DISPLAY = "+91 80000 13966";
const SUPPORT_PHONE_TEL = "+918000013966";
const INSTAGRAM_URL = "https://www.instagram.com/messengerofshiva/";
const SUPPORT_EMAIL = "astrologykuber@gmail.com";

export default function ShippingPolicyPage() {
  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 py-10 sm:py-14" data-testid="shipping-page">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted hover:text-[#3F2D1B] mb-6" data-testid="shipping-back">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-semibold mb-2">
          AstroKuberChat &middot; Policies
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3F2D1B] leading-tight">
          Shipping &amp; Exchange Policy
        </h1>
        <p className="text-xs text-muted mt-3">Effective date: 21 August 2026 &middot; Last updated: 21 August 2026</p>

        {/* Quick promise row */}
        <div className="grid grid-cols-2 gap-3 mt-8">
          <div className="rounded-md border border-amber-300 bg-[#FFF6E0] p-3 text-center">
            <Truck size={20} weight="duotone" className="text-amber-700 mx-auto" />
            <div className="text-xs font-medium text-[#3F2D1B] mt-1">Insured shipping</div>
            <div className="text-[10px] text-muted">3–5 business days · India</div>
          </div>
          <div className="rounded-md border border-amber-300 bg-[#FFF6E0] p-3 text-center">
            <ShieldCheck size={20} weight="duotone" className="text-amber-700 mx-auto" />
            <div className="text-xs font-medium text-[#3F2D1B] mt-1">7-day exchange</div>
            <div className="text-[10px] text-muted">If untouched &amp; unworn</div>
          </div>
        </div>

        <div className="mt-10 space-y-1" data-testid="shipping-content">

          <section className={SECTION}>
            <h2 className={H2}>1. Scope</h2>
            <p className={P}>
              This policy applies to <b>physical items</b> shipped by the
              AstroKuberChat Store — primarily gemstones and gemstone
              accessories. Digital products (PDF reports, chat minutes,
              wallet top-ups) do not have a shipping step.
            </p>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>2. Order Processing</h2>
            <ul className={UL}>
              <li>Orders placed <b>before 6 PM IST</b> on a business day (Mon–Sat) are packed and dispatched the same day.</li>
              <li>Orders placed after 6 PM IST, or on Sundays / national holidays, are dispatched the next working day.</li>
              <li>You will receive a dispatch confirmation with the courier's tracking link on Instagram DM / email / SMS.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>3. Shipping Partners &amp; Timelines</h2>
            <ul className={UL}>
              <li><b>Within India:</b> We ship via reputed insured couriers (Blue Dart, DTDC, or India Post EMS depending on serviceability). Typical delivery: <b>3–5 business days</b> after dispatch.</li>
              <li><b>International:</b> DHL / FedEx / EMS International. Typical delivery: <b>7–14 business days</b> after dispatch, depending on destination country and customs clearance.</li>
              <li><b>Remote areas:</b> Delivery to Andaman &amp; Nicobar, Lakshadweep, and remote parts of the Northeast may add 2–4 extra working days.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>4. Shipping Charges</h2>
            <ul className={UL}>
              <li><b>India:</b> Insured shipping is <b>free</b> on every gemstone order. The price you see on the product page is what you pay.</li>
              <li><b>International:</b> Shipping charges depend on destination and are calculated at checkout. Any customs duties / import taxes charged by the destination country are the buyer's responsibility.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>5. Insurance &amp; Signature-on-Delivery</h2>
            <p className={P}>
              Every stone above ₹5,000 ships fully insured against loss and
              in-transit damage, with signature-on-delivery. This is why we
              cannot deliver to a general "guard-house" address — please
              provide the address of the person who will personally receive
              and sign for the parcel.
            </p>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>6. Order Tracking</h2>
            <p className={P}>
              Once your order is dispatched, you'll receive a tracking link.
              Please allow 6–12 hours after dispatch for the first tracking
              update to appear on the courier's website. If the tracker
              doesn't move for 48 hours, ping us on Instagram DM and we'll
              chase the courier on your behalf.
            </p>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>7. Exchanges</h2>
            <ul className={UL}>
              <li><b>Window:</b> You have <b>7 days from delivery</b> to request an exchange.</li>
              <li><b>Eligibility:</b> The stone, its box, and the government-lab certificate must all be untouched — no scratches, no scuffs, seal intact. Stones that have been mounted (into a ring / pendant), engraved, or worn on the skin are <b>not eligible</b>.</li>
              <li><b>How it works:</b>
                <ol className="list-decimal pl-6 mt-2 space-y-1">
                  <li>DM us on Instagram or call the support number below with your order ID and the reason for exchange.</li>
                  <li>We schedule a free reverse pickup (India). Please repack the stone in its original box.</li>
                  <li>Once the returned stone reaches us and passes inspection (1–2 business days), we dispatch the replacement piece. If the new stone is of higher value, we invoice the difference; if lower, we refund the difference to your original payment method.</li>
                </ol>
              </li>
              <li><b>International exchanges:</b> Return shipping costs are borne by the buyer for international exchanges.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>8. Damaged / Wrong Item on Arrival</h2>
            <p className={P}>
              Please open the parcel on camera. If you receive a damaged
              stone, a wrong SKU, or a mismatched certificate:
            </p>
            <ul className={UL}>
              <li>Do not use the stone.</li>
              <li>Contact us within <b>48 hours of delivery</b> with clear photographs and the unboxing video.</li>
              <li>We ship a replacement free of cost or refund the entire order (buyer's choice) after quality-team review.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>9. Failed Delivery / Undelivered Parcels</h2>
            <p className={P}>
              If the courier is unable to deliver (address unreachable,
              buyer unavailable at 3 attempts, or refused delivery) and the
              parcel is returned to us, we will contact you to arrange a
              re-dispatch. Actual reverse-shipping charges (typically ₹150–₹300)
              may be recovered before the second attempt.
            </p>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>10. Talk to Us</h2>
            <div className="rounded-md border border-amber-300 bg-[#FFF6E0] p-4 sm:p-5 mt-2 space-y-2">
              <div className="text-sm text-[#3F2D1B]">
                <Phone size={14} weight="fill" className="inline mr-2 text-amber-700" />
                Call / WhatsApp <a href={`tel:${SUPPORT_PHONE_TEL}`} className="font-semibold hover:underline">{SUPPORT_PHONE_DISPLAY}</a>
              </div>
              <div className="text-sm text-[#3F2D1B]">
                <InstagramLogo size={14} weight="fill" className="inline mr-2 text-amber-700" />
                DM <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">@messengerofshiva</a> on Instagram
              </div>
              <div className="text-sm text-[#3F2D1B]">
                <Envelope size={14} weight="fill" className="inline mr-2 text-amber-700" />
                Email <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold hover:underline">{SUPPORT_EMAIL}</a>
              </div>
              <div className="text-xs text-muted pt-1">
                Support hours · Mon–Sat, 10 AM – 8 PM IST
              </div>
            </div>
          </section>

          <p className="text-xs text-muted mt-10">
            This policy works in tandem with our{" "}
            <Link to="/refund" className="text-amber-700 underline decoration-dotted">Cancellation &amp; Refund Policy</Link>. In case of a
            conflict between the two, the more buyer-favourable term applies.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
